import { DOCUMENT } from '@angular/common'
import { Injectable, inject } from '@angular/core'

export interface PrintElementOptions {
  appendPrintElements?: () => HTMLElement[]
  printClassName?: string
}

@Injectable({
  providedIn: 'root'
})
export class PrintElementService {
  private static readonly printModeBodyClass = 'se-po-print-element-active'
  private static readonly cleanupFallbackDelayMs = 30000
  private static readonly printHostId = 'se-po-print-element-host'
  private static readonly printRootAttribute = 'data-se-po-print-root'
  private static readonly printStyleId = 'se-po-print-element-styles'

  private document = inject(DOCUMENT)
  private activeCleanup: (() => void) | null = null

  public printElement(element: HTMLElement, options: PrintElementOptions = {}): void {
    const view = this.document.defaultView

    if (!element || !view) {
      return
    }

    this.activeCleanup?.()

    this.ensureBaseStyles()
    const originalParent = element.parentNode
    const originalNextSibling = element.nextSibling
    const printHost = this.getOrCreatePrintHost()

    if (!originalParent) {
      return
    }

    let cleanupFallbackId: number | null = null
    const appendedPrintElements = options.appendPrintElements?.() ?? []

    if (options.printClassName) {
      element.classList.add(options.printClassName)
    }

    element.setAttribute(PrintElementService.printRootAttribute, 'true')
    printHost.appendChild(element)
    appendedPrintElements.forEach(appendedElement => element.appendChild(appendedElement))
    this.document.body.classList.add(PrintElementService.printModeBodyClass)

    let cleanedUp = false

    const cleanup = (): void => {
      if (cleanedUp) {
        return
      }

      cleanedUp = true
      if (originalNextSibling?.parentNode === originalParent) {
        originalParent.insertBefore(element, originalNextSibling)
      } else {
        originalParent.appendChild(element)
      }

      if (options.printClassName) {
        element.classList.remove(options.printClassName)
      }

      if (cleanupFallbackId !== null) {
        view.clearTimeout(cleanupFallbackId)
      }

      appendedPrintElements.forEach(appendedElement => appendedElement.remove())
      element.removeAttribute(PrintElementService.printRootAttribute)
      this.document.body.classList.remove(PrintElementService.printModeBodyClass)
      removeAfterPrintListener()
      this.activeCleanup = null
    }

    const afterPrintHandler = (): void => cleanup()
    view.addEventListener('afterprint', afterPrintHandler, { once: true })

    const removeAfterPrintListener = (): void => {
      view.removeEventListener('afterprint', afterPrintHandler)
    }

    this.activeCleanup = cleanup

    cleanupFallbackId = view.setTimeout(() => cleanup(), PrintElementService.cleanupFallbackDelayMs)

    const print = (): void => {
      view.focus()
      try {
        view.print()
      } catch {
        cleanup()
      }
    }

    if (view.requestAnimationFrame) {
      view.requestAnimationFrame(() => print())
    } else {
      view.setTimeout(() => print(), 0)
    }
  }

  private ensureBaseStyles(): void {
    if (this.document.getElementById(PrintElementService.printStyleId)) {
      return
    }

    const styleElement = this.document.createElement('style')

    styleElement.id = PrintElementService.printStyleId
    styleElement.textContent = `
      @media print {
        html,
        body {
          margin: 0 !important;
          padding: 0 !important;
          background: #ffffff !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        body.${PrintElementService.printModeBodyClass} * {
          visibility: hidden !important;
        }

        body.${PrintElementService.printModeBodyClass} > :not(#${PrintElementService.printHostId}) {
          display: none !important;
        }

        body.${PrintElementService.printModeBodyClass} #${PrintElementService.printHostId},
        body.${PrintElementService.printModeBodyClass} #${PrintElementService.printHostId} * {
          visibility: visible !important;
        }

        body.${PrintElementService.printModeBodyClass} #${PrintElementService.printHostId} {
          position: static !important;
          display: block !important;
          width: 100% !important;
          height: auto !important;
          min-height: auto !important;
          padding: 0 !important;
          margin: 0 !important;
          overflow: visible !important;
          background: #ffffff !important;
        }

        body.${PrintElementService.printModeBodyClass} [${PrintElementService.printRootAttribute}],
        body.${PrintElementService.printModeBodyClass} [${PrintElementService.printRootAttribute}] * {
          visibility: visible !important;
        }

        body.${PrintElementService.printModeBodyClass} [${PrintElementService.printRootAttribute}] {
          position: static !important;
          inset: auto !important;
          display: block !important;
          width: 100% !important;
          height: auto !important;
          min-height: auto !important;
          margin: 0 !important;
          padding: 24px !important;
          box-sizing: border-box !important;
          max-width: none !important;
          overflow: visible !important;
          background: #ffffff !important;
        }

        @page {
          margin: 12mm;
        }
      }
    `

    this.document.head.appendChild(styleElement)
  }

  private getOrCreatePrintHost(): HTMLElement {
    const existingHost = this.document.getElementById(PrintElementService.printHostId)

    if (existingHost) {
      return existingHost
    }

    const printHost = this.document.createElement('div')
    printHost.id = PrintElementService.printHostId
    this.document.body.appendChild(printHost)

    return printHost
  }
}
